import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, Phone } from 'lucide-react';
import { LangToggle } from './LangToggle';

const NAV = [
  { label: 'HOME', to: '/', anchor: '' },
  { label: 'SERVICES', to: '/services', anchor: '' },
  { label: 'DEALERS & FLEETS', to: '/dealers', anchor: '' },
  { label: 'WHY CHOOSE US', to: '/why-choose-us', anchor: '' },
  { label: 'LOCATIONS', to: '/locations', anchor: '' },
  { label: 'CONTACT', to: '/contact', anchor: '' },
];

export default function Navbar({ overlay = false }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const transparent = overlay && !open;

  // Match the homepage logo everywhere: same transparent PNG + size on every page/state.
  // Only the subtle shadow (for legibility over the hero) is context-specific.
  const logoSrc = '/logo-transparent.png';
  const logoCls = 'h-[44px] lg:h-[74px] w-auto object-contain';

  return (
    <header className={`${overlay ? `absolute top-0 inset-x-0 ${open ? 'bg-white' : 'bg-transparent'}` : 'bg-white sticky top-0 border-b border-slate-100'} z-40 transition-colors duration-300`}>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 h-[72px] lg:h-[110px] flex items-center justify-between gap-6">
        {/* Logo — fixed position & size, independent of menu state */}
        <Link to="/" className="flex items-center shrink-0" aria-label="A+ Auto Glass">
          <img src={logoSrc} alt="A+ Auto Glass" className={logoCls} />
        </Link>

        {/* Centered nav */}
        <nav className="hidden lg:flex items-center gap-5 xl:gap-10 flex-1 justify-center">
          {NAV.map((n) => {
            const isRoute = !n.anchor;
            const active = (n.label === 'HOME' && loc.pathname === '/') ||
              (isRoute && n.to !== '/' && loc.pathname === n.to);
            const baseCls = `relative text-center leading-tight text-[12px] xl:text-[13px] font-semibold tracking-[0.1em] xl:tracking-[0.14em] transition-colors py-1 ${
              active ? 'text-[#0D5BD7]' : 'text-[#111827] hover:text-[#0D5BD7]'
            }`;
            const underline = active && (
              <span className="absolute left-0 right-0 -bottom-1 h-[3px] bg-[#0D5BD7] rounded-full" />
            );
            if (isRoute && n.to !== '/') {
              return (
                <Link key={n.label} to={n.to} className={baseCls}>
                  {n.label}
                  {underline}
                </Link>
              );
            }
            if (isRoute && n.to === '/') {
              return (
                <Link key={n.label} to="/" className={baseCls}>
                  {n.label}
                  {underline}
                </Link>
              );
            }
            return (
              <a
                key={n.label}
                href={loc.pathname === '/' ? n.anchor : `/${n.anchor}`}
                className={baseCls}
              >
                {n.label}
                {underline}
              </a>
            );
          })}
        </nav>

        <LangToggle className="hidden lg:flex shrink-0" testId="lang-toggle-desktop" />

        {/* Mobile */}
        <div className="lg:hidden flex items-center gap-2">
        <LangToggle testId="lang-toggle-mobile" />
        <button
          className={`lg:hidden w-11 h-11 -mr-2 rounded-full flex items-center justify-center transition-colors ${transparent ? 'text-[#111827] bg-[#F1F3F7] active:bg-slate-200' : 'text-[#111827] active:bg-slate-100'}`}
          onClick={() => setOpen(!open)}
          aria-label="Menu"
          aria-expanded={open}
          data-testid="mobile-menu-btn"
        >
          {open ? <X /> : <Menu />}
        </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white px-5 pt-3 pb-6" data-testid="mobile-menu">
          {NAV.map((n, i) => {
            const active = loc.pathname === n.to;
            return (
              <Link
                key={n.label}
                to={n.to}
                onClick={() => setOpen(false)}
                style={{ animationDelay: `${i * 40}ms` }}
                className={`flex items-center justify-between h-[52px] border-b border-slate-100 text-[15px] font-bold tracking-[0.12em] animate-[navIn_0.3s_ease-out_both] ${active ? 'text-[#0D5BD7]' : 'text-[#111827]'}`}
              >
                {n.label}
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </Link>
            );
          })}
          <LangToggle className="mt-4 justify-center text-[14px]" testId="lang-toggle-menu" />
          <a
            href="tel:+13435588820"
            className="mt-4 h-[52px] rounded-full bg-[#111827] text-white flex items-center justify-center gap-2 text-[13px] font-bold tracking-[0.18em]"
            data-testid="mobile-menu-call"
          >
            <Phone className="w-4 h-4" /> 343.558.8820
          </a>
          <style>{`@keyframes navIn { from { opacity: 0; transform: translateY(-6px) } to { opacity: 1; transform: none } }`}</style>
        </div>
      )}
    </header>
  );
}
